import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { JjamesButtonComponent } from 'shared-lib';
import {
    CreateSiteMappingRequest,
    ParkingSpaceDto,
    ParkingTypeDefnitionDto,
    ParkingTypeService,
    ParkingZoneDto,
    SiteMappingDto,
    SiteMappingService,
    UpdateSiteMappingRequest
} from '../../../../api/gen';

export interface SiteMappingDialogData {
    parkingSpace: ParkingSpaceDto;
    allParkingSpaces: ParkingSpaceDto[];
}

/**
 * Manages the old-DB site mappings that the migration sync uses to resolve an old
 * `parkingsite_id` to a new ParkingSpace + ParkingTypeDefinition (+ optional zone).
 *
 * Without a mapping the sync skips the old site entirely, so those rows simply never arrive —
 * which looks like an import bug rather than missing configuration. This dialog is the only
 * place such rows can be created outside of hand-written SQL.
 *
 * Two things the API shape forces on this UI:
 *
 * - **Site-level only on create.** `SiteMapping.OldParkingRightId` distinguishes a site-level
 *   mapping (null) from a right-level one, used for POOL rows that need per-zone granularity.
 *   Neither the create nor the update request carries that field, so anything created here is
 *   site-level. Right-level rows are still listed and can be edited safely — the update handler
 *   never touches `OldParkingRightId` — they just cannot be created here.
 *
 * - **Edits are restricted to the current space.** Update replaces `NewParkingSpaceId` with
 *   whatever the request carries, and this dialog only knows the zones of the space it was
 *   opened from. Editing another space's mapping here would silently move it to this space, so
 *   those rows are shown read-only instead.
 */
@Component({
    selector: 'app-site-mapping-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        JjamesButtonComponent,
        TranslatePipe
    ],
    templateUrl: './site-mapping-dialog.component.html'
})
export class SiteMappingDialogComponent {
    private dialogRef = inject(MatDialogRef<SiteMappingDialogComponent>);
    private siteMappingService = inject(SiteMappingService);
    private parkingTypeService = inject(ParkingTypeService);
    private snackBar = inject(MatSnackBar);
    private translate = inject(TranslateService);
    readonly data = inject<SiteMappingDialogData>(MAT_DIALOG_DATA);

    readonly zones: ParkingZoneDto[] = this.data.parkingSpace.parkingZones ?? [];

    mappings = signal<SiteMappingDto[]>([]);
    parkingTypes = signal<ParkingTypeDefnitionDto[]>([]);

    isLoadingMappings = signal(false);
    isLoadingTypes = signal(false);
    isSaving = signal(false);
    deletingId = signal<string | null>(null);
    pendingDeleteId = signal<string | null>(null);
    editingId = signal<string | null>(null);

    /** Mappings are global; the list defaults to the space the dialog was opened from. */
    scope = signal<'space' | 'all'>('space');

    // Form state
    oldParkingSiteId = signal<number | null>(null);
    newParkingTypeDefinitionId = signal<string>('');
    newParkingZoneId = signal<string>('');
    enabled = signal(true);
    description = signal('');

    /** True while the form is editing an existing row rather than creating one. */
    isEditing = computed(() => this.editingId() !== null);

    visibleMappings = computed(() => {
        const all = this.mappings();
        if (this.scope() === 'all') return all;
        return all.filter(mapping => mapping.newParkingSpaceId === this.data.parkingSpace.id);
    });

    /**
     * Mirrors the server's duplicate rule (same old site + space + type + zone) so the clash is
     * visible before the request is sent rather than as a 400 afterwards.
     */
    isDuplicate = computed(() => {
        const oldSiteId = this.oldParkingSiteId();
        if (oldSiteId === null) return false;

        const zoneId = this.newParkingZoneId() || null;
        const typeId = this.newParkingTypeDefinitionId();
        const editingId = this.editingId();

        return this.mappings().some(mapping =>
            mapping.id !== editingId
            && mapping.oldParkingSiteId === Number(oldSiteId)
            && mapping.newParkingSpaceId === this.data.parkingSpace.id
            && mapping.newParkingTypeDefinitionId === typeId
            && (mapping.newParkingZoneId ?? null) === zoneId
        );
    });

    canSave = computed(() => {
        const oldSiteId = this.oldParkingSiteId();
        return oldSiteId !== null
            && Number.isInteger(Number(oldSiteId))
            && Number(oldSiteId) >= 0
            && !!this.newParkingTypeDefinitionId()
            && !this.isDuplicate()
            && !this.isSaving();
    });

    constructor() {
        this.loadParkingTypes();
        this.loadMappings();
    }

    /**
     * The mapping stores a ParkingTypeDefinition id, not a ParkingType instance id, so the
     * options come from the definitions endpoint rather than from the caller's type list.
     */
    private loadParkingTypes(): void {
        this.isLoadingTypes.set(true);
        this.parkingTypeService.parkingTypesGet().subscribe({
            next: (types) => {
                this.parkingTypes.set(types ?? []);
                this.isLoadingTypes.set(false);
            },
            error: (error) => {
                console.error('Error loading parking type definitions', error);
                this.snackBar.open(this.translate.instant('siteMapping.typesFailed'), 'Close', { duration: 4000 });
                this.isLoadingTypes.set(false);
            }
        });
    }

    /** `enabledOnly: false` returns disabled rows too — a disabled mapping is why a site stops syncing. */
    loadMappings(): void {
        this.isLoadingMappings.set(true);
        this.siteMappingService.siteMappingsGet(false).subscribe({
            next: (result) => {
                this.mappings.set(result ?? []);
                this.isLoadingMappings.set(false);
            },
            error: (error) => {
                console.error('Error loading site mappings', error);
                this.snackBar.open(this.translate.instant('siteMapping.loadFailed'), 'Close', { duration: 4000 });
                this.isLoadingMappings.set(false);
            }
        });
    }

    /** Only rows of the space this dialog was opened from can be changed here. */
    isEditable(mapping: SiteMappingDto): boolean {
        return mapping.newParkingSpaceId === this.data.parkingSpace.id;
    }

    startEdit(mapping: SiteMappingDto): void {
        if (!this.isEditable(mapping)) return;

        this.editingId.set(mapping.id ?? null);
        this.pendingDeleteId.set(null);
        this.oldParkingSiteId.set(mapping.oldParkingSiteId ?? null);
        this.newParkingTypeDefinitionId.set(mapping.newParkingTypeDefinitionId ?? '');
        this.newParkingZoneId.set(mapping.newParkingZoneId ?? '');
        this.enabled.set(mapping.enabled ?? true);
        this.description.set(mapping.description ?? '');
    }

    cancelEdit(): void {
        this.resetForm();
    }

    save(): void {
        if (!this.canSave()) return;

        this.isSaving.set(true);

        const payload: CreateSiteMappingRequest & UpdateSiteMappingRequest = {
            oldParkingSiteId: Number(this.oldParkingSiteId()),
            newParkingSpaceId: this.data.parkingSpace.id,
            newParkingTypeDefinitionId: this.newParkingTypeDefinitionId(),
            newParkingZoneId: this.newParkingZoneId() || null,
            enabled: this.enabled(),
            description: this.description().trim() || null
        };

        const editingId = this.editingId();
        const request$ = editingId
            ? this.siteMappingService.siteMappingsIdPut(editingId, payload)
            : this.siteMappingService.siteMappingsPost(payload);

        request$.subscribe({
            next: () => {
                this.snackBar.open(
                    this.translate.instant(editingId ? 'siteMapping.updatedOk' : 'siteMapping.createdOk'),
                    'Close',
                    { duration: 3000 }
                );
                this.resetForm();
                this.loadMappings();
                this.isSaving.set(false);
            },
            error: (error) => {
                console.error('Error saving site mapping', error);
                this.snackBar.open(this.translate.instant('siteMapping.saveFailed'), 'Close', { duration: 4000 });
                this.isSaving.set(false);
            }
        });
    }

    askDelete(mapping: SiteMappingDto): void {
        if (!this.isEditable(mapping)) return;
        this.pendingDeleteId.set(mapping.id ?? null);
    }

    cancelDelete(): void {
        this.pendingDeleteId.set(null);
    }

    confirmDelete(mapping: SiteMappingDto): void {
        if (!mapping.id || !this.isEditable(mapping)) return;

        this.deletingId.set(mapping.id);
        this.siteMappingService.siteMappingsIdDelete(mapping.id).subscribe({
            next: () => {
                this.snackBar.open(this.translate.instant('siteMapping.deletedOk'), 'Close', { duration: 3000 });
                if (this.editingId() === mapping.id) {
                    this.resetForm();
                }
                this.pendingDeleteId.set(null);
                this.deletingId.set(null);
                this.loadMappings();
            },
            error: (error) => {
                console.error('Error deleting site mapping', error);
                this.snackBar.open(this.translate.instant('siteMapping.deleteFailed'), 'Close', { duration: 4000 });
                this.deletingId.set(null);
            }
        });
    }

    typeName(typeId?: string | null): string {
        if (!typeId) return '—';
        return this.parkingTypes().find(type => type.id === typeId)?.name ?? typeId;
    }

    zoneName(mapping: SiteMappingDto): string {
        if (!mapping.newParkingZoneId) return this.translate.instant('siteMapping.allZones');

        const zone = this.zones.find(item => item.id === mapping.newParkingZoneId);
        if (zone) return zone.name;

        const space = this.data.allParkingSpaces.find(item => item.id === mapping.newParkingSpaceId);
        return space?.parkingZones?.find(item => item.id === mapping.newParkingZoneId)?.name
            ?? mapping.newParkingZoneId;
    }

    spaceName(mapping: SiteMappingDto): string {
        if (!mapping.newParkingSpaceId) return '—';
        return this.data.allParkingSpaces.find(space => space.id === mapping.newParkingSpaceId)?.name
            ?? mapping.newParkingSpaceId;
    }

    close(): void {
        this.dialogRef.close();
    }

    private resetForm(): void {
        this.editingId.set(null);
        this.pendingDeleteId.set(null);
        this.oldParkingSiteId.set(null);
        this.newParkingTypeDefinitionId.set('');
        this.newParkingZoneId.set('');
        this.enabled.set(true);
        this.description.set('');
    }
}
